@file:OptIn(ExperimentalJsExport::class)

package co.diena.everything.app.worker

import co.diena.everything.api.Bucket
import co.diena.everything.api.EntityId
import co.diena.everything.api.EverythingApi
import co.diena.everything.app.worker.WorkerMessage.*
import co.diena.everything.app.worker.util.ApiEventLoader
import co.touchlab.kermit.Logger
import io.ktor.client.HttpClient
import io.ktor.client.plugins.websocket.WebSockets
import io.ktor.client.request.url
import io.ktor.http.URLBuilder
import io.ktor.http.URLProtocol
import io.ktor.http.Url
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineExceptionHandler
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.GlobalScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.cancel
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.collectLatest
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.conflate
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.fold
import kotlinx.coroutines.flow.mapNotNull
import kotlinx.coroutines.flow.transform
import kotlinx.coroutines.launch
import kotlinx.rpc.krpc.ktor.client.installKrpc
import kotlinx.rpc.krpc.ktor.client.rpc
import kotlinx.rpc.krpc.ktor.client.rpcConfig
import kotlinx.rpc.krpc.serialization.json.json
import kotlinx.rpc.withService
import org.w3c.dom.AbstractWorker
import org.w3c.dom.DedicatedWorkerGlobalScope
import org.w3c.dom.MessageEvent
import org.w3c.dom.WorkerOptions
import org.w3c.dom.events.Event
import org.w3c.dom.events.EventTarget
import org.w3c.dom.url.URLSearchParams

private val log = Logger.withTag("Worker")

public external open class Worker(scriptURL: String, options: WorkerOptions = definedExternally) :
    EventTarget, AbstractWorker {
    var onmessage: ((MessageEvent) -> dynamic)?
    override var onerror: ((Event) -> dynamic)?
    fun terminate()
    fun postMessage(message: Any?, transfer: Array<dynamic> = definedExternally)
}

@JsExport
class WorkerApiImpl(private val worker: Worker) : WorkerApi {

    override fun sendMessage(message: String) {
        worker.postMessage(message)
    }
}

@JsExport
fun createWorkerImpl(
    onMessage: (String) -> Unit,
): WorkerApiImpl {
    val worker = Worker("worker.js")
    worker.onmessage = { messageEvent ->
        val dataString = messageEvent.data as? String
        dataString?.let {
            onMessage(dataString)
        }
    }
    worker.onerror = {
        println("Worker error: ${it.type}")
        println(it)
    }
    return WorkerApiImpl(worker)
}

// Worker impl

fun worker(block: WorkerScope.() -> Unit) {
    val isWorkerGlobalScope = js("typeof(WorkerGlobalScope) !== \"undefined\"") as? Boolean
        ?: throw IllegalStateException("Boolean cast went wrong")
    if (!isWorkerGlobalScope) return

    val self = js("self") as? DedicatedWorkerGlobalScope
        ?: throw IllegalStateException("DedicatedWorkerGlobalScope cast went wrong")
    val scope = WorkerScope(self)
    try {
        block(scope)
    } catch (e: Throwable) {
        //  self.postMessage("Worker error: ${e.message}")
    } finally {
        //  self.postMessage("Worker finished")
    }
}

class WorkerScope(internal val self: DedicatedWorkerGlobalScope) {
    val workerId = URLSearchParams(self.location.search).get("id") ?: "Unknown worker"
}


fun main() {
    worker {

        val client = HttpClient {
            install(WebSockets)
            installKrpc()
        }
        val href = self.location.href
        val hrefUrl = Url(href)
        val rpcBackendUrl = URLBuilder(
            protocol = if (hrefUrl.protocol == URLProtocol.HTTPS) URLProtocol.WSS else URLProtocol.WS,
            host = hrefUrl.host,
            port = hrefUrl.port,
            pathSegments = listOf("api")
        ).build()


        val clientStateFlow = makeClientStateFlow(
            rpcBackendUrl,
            client
        )

        var loaderScope: CoroutineScope? = null
        var loader: ApiEventLoader? = null
        var lastEventRequest: List<Bucket>? = null
        var lastCategoryRequest: EntityId? = null
        var job: Job? = null

        fun loadEvents(loader: ApiEventLoader, scope: CoroutineScope, buckets: List<Bucket>) {
            job?.cancel()
            job = scope.launch {
                try {
                    loader.loadEvents(scope, buckets)
                } catch (e: CancellationException) {

                } catch (e: Throwable) {
                    log.e { "Error while loading events: ${e.message}" }
                    self.postMessage(
                        Error(LoaderError.UnknownError).encode()
                    )
                }
            }
        }

        val initializeSettingsFlow = MutableStateFlow<WorkerCommand.Initialize?>(null)
        val visibilityFlow = MutableStateFlow<Boolean>(true)

        val clientJob = GlobalScope.launch {
            initializeSettingsFlow
                .mapNotNull { it }
                .collectLatest { initializeSettings ->
                    visibilityFlow.collectLatest { visible ->
                        if (!visible) {
                            loader = null
                            loaderScope?.cancel()
                            loaderScope = null
                            return@collectLatest
                        }

                        clientStateFlow.collect { state ->
                            when (state) {
                                is ClientState.Connected -> {
                                    val newLoader = ApiEventLoader(
                                        state.client,
                                        initializeSettings.language,
                                        initializeSettings.limitPerBucket
                                    )
                                    loader = newLoader
                                    val newScope = CoroutineScope(
                                        Dispatchers.Default + SupervisorJob() + CoroutineExceptionHandler(
                                            { _, throwable ->
                                                if (throwable is CancellationException) {
                                                    return@CoroutineExceptionHandler
                                                }
                                                log.e { "Error in loader scope: ${throwable.message}" }
                                                self.postMessage(WorkerMessage.Error(LoaderError.UnknownError))
                                            })
                                    )
                                    loaderScope?.cancel()
                                    loaderScope = newScope

                                    setupLoaderListeners(
                                        self,
                                        newLoader,
                                        newScope
                                    )
                                    self.postMessage(WorkerMessage.Error(null).encode())
                                    lastCategoryRequest?.let { category ->
                                        newScope.launch {
                                            newLoader.setCategory(category)
                                        }
                                    }
                                    lastEventRequest?.let { buckets ->
                                        loadEvents(newLoader, newScope, buckets)
                                    }
                                }

                                is ClientState.Disconnected -> {
                                    loader = null
                                    loaderScope?.cancel()
                                    loaderScope = null
                                    self.postMessage(
                                        WorkerMessage.Error(
                                            LoaderError.NetworkError(
                                                state.retryInSeconds ?: 0
                                            )
                                        ).encode()
                                    )
                                }
                            }
                        }
                    }
                }
        }
        clientJob.invokeOnCompletion {
            log.e { "Error while processing api client: ${it?.message}" }
            self.postMessage(
                WorkerMessage.Error(LoaderError.UnknownError).encode()
            )
        }


        self.onmessage = { messageEvent ->

            val dataString = messageEvent.data as? String
            if (dataString != null) {
                when (val command = WorkerCommand.decode(dataString)) {
                    is WorkerCommand.Initialize -> {
                        initializeSettingsFlow.value = command
                    }

                    is WorkerCommand.VisibilityChanged -> {
                        visibilityFlow.value = command.visible
                    }

                    is WorkerCommand.LoadEvents -> {
                        lastEventRequest = command.buckets
                        loader?.let { loader ->
                            loaderScope?.let { scope ->
                                loadEvents(loader, scope, command.buckets)
                            }
                        }
                    }

                    is WorkerCommand.SetCategory -> {
                        lastCategoryRequest = command.category
                        loaderScope?.launch {
                            loader?.setCategory(command.category)
                        }
                    }

                    is WorkerCommand.AnalyticsEventClicked -> {
                        loaderScope?.launch {
                            try {
                                loader?.analyticsEvent(command.entity)
                            } catch (e: CancellationException) {
                                // ignore
                            } catch (e: Throwable) {
                                log.w { "Error while sending analytics event clicked: ${e.message}" }
                            }
                        }
                    }

                    is WorkerCommand.SubscribeToNewsLetter -> {
                        loaderScope?.launch {
                            try {
                                loader?.subscribeToNewsletter(command.email)
                                self.postMessage(
                                    WorkerMessage.NewsletterSubscribed(true).encode()
                                )
                            } catch (e: CancellationException) {
                                // ignore
                            } catch (e: Throwable) {
                                log.w { "Error while subscribing to newsletter: ${e.message}" }
                                self.postMessage(
                                    WorkerMessage.NewsletterSubscribed(false).encode()
                                )
                            }
                        }
                    }
                }
            }
        }


    }
}

fun setupLoaderListeners(
    self: DedicatedWorkerGlobalScope,
    loader: ApiEventLoader,
    scope: CoroutineScope,
) {
    val collectJob = scope.launch {
        loader.events
            .throttleLatest(200)
            .fold(emptySet<EntityId>()) { acc, value ->
                val message = WorkerMessage.EventsLoaded(
                    addEvents = value - acc, // only new events
                    removeEvents = acc - value.keys
                ).encode()
                self.postMessage(message)

                value.keys
            }
    }
    collectJob.invokeOnCompletion {
        if (it !is CancellationException) {
            log.e { "Error while collecting events: ${it?.message}" }
            self.postMessage(
                WorkerMessage.Error(LoaderError.UnknownError).encode()
            )
        }
    }

    val loadingJob = scope.launch {
        combine(
            loader.pendingBucketsCount,
            loader.pendingEntitiesCount
        ) { buckets, entities ->
            buckets > 0 || entities > 0
        }
            .distinctUntilChanged()
            .throttleLatest(200)
            .fold(false) { acc, value ->
                if (acc != value) {
                    val message = WorkerMessage.Loading(value).encode()
                    self.postMessage(message)
                }
                value
            }
    }
    loadingJob.invokeOnCompletion {
        if (it !is CancellationException) {
            log.e { "Error while collecting loading state: ${it?.message}" }
            self.postMessage(
                WorkerMessage.Error(LoaderError.UnknownError).encode()
            )
        }
    }

    val errorJob = scope.launch {
        loader.error
            .throttleLatest(200)
            .fold<LoaderError?, LoaderError?>(null) { acc, value ->
                if (acc != value) {
                    val message = WorkerMessage.Error(value).encode()
                    self.postMessage(message)
                }
                value
            }
    }
    errorJob.invokeOnCompletion {
        if (it !is CancellationException) {
            log.e { "Error while collecting error state: ${it?.message}" }
            self.postMessage(
                WorkerMessage.Error(LoaderError.UnknownError).encode()
            )
        }
    }

    val categoriesJob = scope.launch {
        loader.availableCategories
            .throttleLatest(200)
            .collect {
                val message = WorkerMessage.CategoriesLoaded(it).encode()
                self.postMessage(message)
            }
    }
    categoriesJob.invokeOnCompletion {
        if (it !is CancellationException) {
            log.e { "Error while collecting categories state: ${it?.message}" }
            self.postMessage(
                WorkerMessage.Error(LoaderError.UnknownError).encode()
            )
        }
    }
}

fun <T> Flow<T>.throttleLatest(delayMillis: Long): Flow<T> = this
    .conflate()
    .transform {
        emit(it)
        delay(delayMillis)
    }
